// Impact (« ma part ») : fraction exacte entre 0 et 1, échangée avec l'API en chaîne « n/d »
// simplifiée ("1/1", "1/2", "0/1", "617/1622"). Cf. ImpactFraction côté API.

export const FULL_IMPACT = '1/1'

// Choix rapides du sélecteur, les autres parts passent par la saisie libre.
export const QUICK_IMPACTS: { impact: Impact, label: string }[] = [
    { impact: '1/1', label: '100' },
    { impact: '1/2', label: '½' },
    { impact: '0/1', label: '0' },
]

const GLYPHS: Record<string, string> = { '1/2': '½', '1/3': '⅓', '2/3': '⅔', '1/4': '¼', '3/4': '¾' }

const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a

export const makeImpact = (numerator: number, denominator: number): Impact => {
    const divisor = gcd(numerator, denominator) || 1
    return `${numerator / divisor}/${denominator / divisor}`
}

export const impactParts = (impact: Impact): [number, number] => {
    const [numerator, denominator] = impact.split('/').map(Number)
    return denominator > 0 ? [numerator, denominator] : [1, 1]
}

// Part d'un montant, non arrondie : on n'arrondit qu'une fois, sur le total.
export const applyImpact = (amountCents: number, impact: Impact) => {
    const [numerator, denominator] = impactParts(impact)
    return amountCents * numerator / denominator
}

export const addImpacts = (a: Impact, b: Impact) => {
    const [an, ad] = impactParts(a), [bn, bd] = impactParts(b)
    return makeImpact(an * bd + bn * ad, ad * bd)
}

// « 100 % », « 0 », « ½ », « 3/5 », ou « 38,0 % » pour une part Tricount inégale.
export const formatImpact = (impact: Impact) => {
    const [numerator, denominator] = impactParts(impact)
    if(numerator === 0) {
        return '0'
    }
    if(numerator === denominator) {
        return '100 %'
    }
    const key = `${numerator}/${denominator}`
    if(GLYPHS[key]) {
        return GLYPHS[key]
    }
    if(denominator <= 12) {
        return key
    }
    return `${(100 * numerator / denominator).toFixed(1).replace('.', ',')} %`
}

const euros = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`

// Saisie libre : « 2/3 », « 25 % » ou « 25 », et « 12,50 € » (fraction du montant de l'opération,
// si on le connaît). Renvoie la part, ou un message d'erreur.
export const parseImpactInput = (text: string, amountCents?: number): { impact: Impact, detail?: string } | { error: string } => {
    const value = text.trim().replace(',', '.')
    let match
    if((match = value.match(/^(\d+)\s*\/\s*(\d+)$/))) {
        const numerator = Number(match[1]), denominator = Number(match[2])
        if(denominator === 0 || numerator > denominator) {
            return { error: 'Entre 0 et 1 : le numérateur ne dépasse pas le dénominateur' }
        }
        return { impact: makeImpact(numerator, denominator) }
    }
    if((match = value.match(/^(\d+(?:\.\d+)?)\s*€$/))) {
        const total = Math.abs(amountCents || 0)
        if(total === 0) {
            return { error: 'Pas de montant en euros ici : une fraction ou un pourcentage' }
        }
        const cents = Math.round(Number(match[1]) * 100)
        if(cents > total) {
            return { error: `Au plus ${euros(total)}, le montant de l'opération` }
        }
        return { impact: makeImpact(cents, total), detail: `${euros(cents)} sur ${euros(total)}` }
    }
    if((match = value.match(/^(\d+(?:\.\d+)?)\s*%?$/))) {
        const percent = Number(match[1])
        if(percent > 100) {
            return { error: 'Au plus 100 %' }
        }
        const scale = 10 ** (match[1].split('.')[1] || '').length
        return { impact: makeImpact(Math.round(percent * scale), 100 * scale) }
    }
    return { error: amountCents ? 'Ex. 2/3, 25 % ou 12,50 €' : 'Ex. 2/3 ou 25 %' }
}
